import { useInfiniteQuery } from '@tanstack/react-query';
import useAppUser from 'hooks/useAppUser';
import getPropertiesQuery, {
  PropertiesQueryFilter,
} from 'utils/property/getPropertiesQuery';
import { propertyForBodyQueryFields } from 'pages/agencyProperties/body/PropertiesBody.constants';
import API from 'services/API';
import { PropertyForPropertyListItem } from 'pages/agencyProperties/Properties.types';

interface GetPaginatedPropertiesResponseTO {
  data: {
    properties: PropertyForPropertyListItem[];
  };
}

interface QueryData {
  properties: PropertyForPropertyListItem[];
  offset: number;
}

export const PAGINATED_PROPERTIES_QUERY_KEY = 'paginated-properties';
const PAGE_SIZE = 10;

const useFetchPaginatedProperties = (
  filters: Omit<PropertiesQueryFilter, 'offset' | 'limit'>,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const { agencyUid, isOwner } = useAppUser();

  const infiniteQuery = useInfiniteQuery<QueryData>({
    queryKey: [PAGINATED_PROPERTIES_QUERY_KEY, agencyUid, filters],
    queryFn: async ({ pageParam: offset = 0 }) => {
      const conditions = getPropertiesQuery({
        filters: { ...filters, offset, limit: PAGE_SIZE },
      });

      const conditionsWithAgencyUid = isOwner
        ? conditions
        : `agencyUid:"${agencyUid}",${conditions}`;

      const response = await API.post<GetPaginatedPropertiesResponseTO>(
        '/v3/graphql',
        {
          operationName: 'PaginatedPropertiesList',
          query: `{
            properties(${conditionsWithAgencyUid}) {
              ${propertyForBodyQueryFields}
            }
          }`,
        },
      );

      return {
        properties: response.data.data.properties ?? [],
        offset,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.properties.length < PAGE_SIZE) {
        return undefined;
      }

      return lastPage.offset + PAGE_SIZE;
    },
    enabled,
    staleTime: 30000,
  });

  const allLoadedProperties =
    infiniteQuery.data?.pages?.flatMap(({ properties }) => properties) ?? [];

  return {
    ...infiniteQuery,
    properties: allLoadedProperties,
  };
};

export default useFetchPaginatedProperties;
